import { Reveal } from "./Reveal";
import { Zap, ArrowRight } from "lucide-react";
import { scrollTo } from "@/lib/site";

const STEPS = [
  ["00:00", "Tell us your requirement", "You tell us about your business and website requirements."],
  ["04:00", "Design direction", "We finalize the website structure and visual direction."],
  ["12:00", "Development", "We build your responsive website."],
  ["20:00", "Review", "You review the website and provide final feedback."],
  ["24:00", "Go live", "Your professional website is ready to launch."],
];

export const Launch24 = () => (
  <section id="launch" data-testid="launch-24-section" className="noise relative overflow-hidden bg-black py-24 md:py-32">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute left-[-10%] top-1/3 h-[50vh] w-[50vw] rounded-full bg-royal/10 blur-[140px]" />
      <div className="absolute bottom-0 right-[-5%] h-[40vh] w-[40vw] rounded-full bg-accent/10 blur-[120px]" />
    </div>
    <div className="relative mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
      <div>
        <Reveal>
          <span className="glass inline-flex items-center gap-2 rounded-full px-4 py-2">
            <Zap className="h-3.5 w-3.5 text-accent" />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-200">Fast Track</span>
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-black uppercase leading-[1.05] tracking-tighter sm:text-4xl lg:text-5xl">
            Get your website live in <span className="text-gradient">24 hours.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-5 text-base text-zinc-400 md:text-lg">Why wait weeks to take your business online?</p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="relative mt-10 select-none">
            <p className="font-display text-[26vw] font-black leading-none tracking-tighter text-transparent lg:text-[11rem]" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.15)" }}>
              24H
            </p>
            <p className="absolute inset-0 animate-pulse-glow bg-gradient-to-b from-accent/40 to-transparent bg-clip-text font-display text-[26vw] font-black leading-none tracking-tighter text-transparent lg:text-[11rem]">
              24H
            </p>
            <p className="mt-2 font-display text-xs font-bold uppercase tracking-[0.4em] text-accent">Go Live</p>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <button
            onClick={() => scrollTo("#enquiry")}
            data-testid="launch-24-start-btn"
            className="group mt-8 flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,box-shadow] duration-300 hover:bg-white hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]"
          >
            Start My 24-Hour Website
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>

      <div className="relative">
        <div className="absolute bottom-8 left-[27px] top-8 w-px bg-gradient-to-b from-accent/60 via-white/15 to-accent/60" />
        {STEPS.map(([time, title, desc], i) => (
          <Reveal key={time} delay={i * 0.1}>
            <div className="relative flex gap-6 py-5" data-testid={`launch-step-${i}`}>
              <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-ink font-display text-[10px] font-bold tracking-wider text-accent shadow-[0_0_25px_rgba(0,229,255,0.2)]">
                {time}
              </div>
              <div className="pt-1">
                <h3 className="font-display text-base font-extrabold uppercase tracking-wide text-white">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">{desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
    <Reveal className="relative mx-auto mt-14 max-w-4xl px-6 md:px-12">
      <p className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-xs leading-relaxed text-zinc-500" data-testid="launch-24-disclaimer">
        24-hour launch is available for eligible standard website projects when required content, branding
        assets and approvals are provided on time. Complex custom websites, e-commerce projects, advanced
        integrations and extensive revisions may require additional time.
      </p>
    </Reveal>
  </section>
);
